import { Box, Checkbox, Divider, Flex, Input, InputGroup, InputRightElement, Text } from '@chakra-ui/react'
import { memo, useMemo, useRef, useState } from 'react'
import { useMembers } from '../../../hooks/useOrganization'
import { capitalizeWords } from '../../../utils/functions/formatters'
import LazyLoadAvatar from '../media/LazyLoadedAvatar'

export interface MemberOption {
  id: string
  name: string
  image: string | null | undefined
}

interface FormMemberMultiSelectProps {
  value: MemberOption[] | undefined
  onChange: (members: MemberOption[]) => void
  onRemove?: (member: MemberOption) => void
  isDisabled?: boolean
  name: string
  placeholder?: string
  height?: string
  maxHeight?: string
  // Let the parent own the label (e.g. when a mode toggle sits beside it).
  hideLabel?: boolean
}

const FormMemberMultiSelect = ({
  value,
  onChange,
  onRemove,
  isDisabled,
  name,
  placeholder,
  height = '40px',
  maxHeight = '200px',
  hideLabel,
}: FormMemberMultiSelectProps) => {
  const [memberSearch, setMemberSearch] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const members = useMembers(
    { page: 1, search: memberSearch, page_size: 15, fields: 'id,name,logo', ordering: 'name' },
    memberSearch.length > 1,
  )

  // Selected members first (alphabetically), then unselected search hits.
  // Selected members stay visible even when they don't match the search.
  const filteredMembers = useMemo(() => {
    const currentValue = value || []
    const selectedIds = new Set(currentValue.map((m) => m.id))
    const searchTerm = memberSearch.toLowerCase()

    const selected = currentValue
      .map((member) => ({ id: member.id, name: member.name, image: member.image }))
      .sort((a, b) => a.name.localeCompare(b.name))

    const unselected =
      members.data?.data
        ?.filter((member) => !selectedIds.has(member.id) && member.name.toLowerCase().includes(searchTerm))
        .map((member) => ({ id: member.id, name: member.name, image: member.logo }))
        .sort((a, b) => a.name.localeCompare(b.name)) || []

    return [...selected, ...unselected]
  }, [members.data?.data, memberSearch, value])

  const isMemberSelected = (member: MemberOption) => (value || []).some((s) => s?.id === member?.id)

  const handleToggleMember = (member: MemberOption) => {
    const currentValue = value || []
    if (isMemberSelected(member)) {
      onChange(currentValue.filter((m) => m.id !== member.id))
      if (onRemove) onRemove(member)
    } else {
      onChange([...currentValue, member])
    }
    // Keep the input focused after selection
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleSearchChange = (searchValue: string) => {
    setMemberSearch(searchValue)
    setIsDropdownOpen(searchValue.length > 0)
  }

  const handleInputFocus = () => {
    if (memberSearch.length > 0 || (value?.length ?? 0) > 0) setIsDropdownOpen(true)
  }

  const handleInputBlur = () => {
    // Use a timeout to allow click events to process first
    setTimeout(() => {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setIsDropdownOpen(false)
        setMemberSearch('')
      }
    }, 150)
  }

  const handleSelectAll = () => {
    const currentValue = value || []
    const existingIds = new Set(currentValue.map((m) => m.id))
    const toAdd = filteredMembers.filter((member) => !existingIds.has(member.id))
    if (toAdd.length === 0) return
    onChange([...currentValue, ...toAdd])
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleUnselectAll = () => {
    if ((value?.length ?? 0) === 0) return
    if (onRemove) (value || []).forEach((member) => onRemove(member))
    onChange([])
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const areAllSelected = filteredMembers.length > 0 && filteredMembers.every((m) => isMemberSelected(m))

  // The dropdown is absolutely positioned below the input, which itself sits
  // below the label. With `hideLabel` there is no label, so drop that offset —
  // otherwise the menu floats 36px too low (CORE-2817).
  const labelOffset = hideLabel ? '0px' : '36px'

  return (
    <Box position="relative" w="100%" ref={containerRef}>
      {!hideLabel && (
        <Text color="neutral.900" fontWeight="400" textStyle="b-sm" mb="8px">
          {capitalizeWords(name)}
        </Text>
      )}

      {/* Search input */}
      <InputGroup>
        <Input
          ref={inputRef}
          value={memberSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          h={{ base: '44px', md: height }}
          aria-label={placeholder || `Search for ${name.toLowerCase()}`}
          placeholder={placeholder || `Search for ${name.toLowerCase()}`}
          fontSize={{ base: '16px', md: '12px' }}
          disabled={isDisabled}
        />
        {(value?.length ?? 0) > 0 && (
          <InputRightElement h={{ base: '44px', md: height }} pr="12px">
            <Flex
              bg="primary.500"
              borderRadius="full"
              h="24px"
              minW="24px"
              px="8px"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="12px" fontWeight="600" color="white">
                {value?.length ?? 0}
              </Text>
            </Flex>
          </InputRightElement>
        )}
      </InputGroup>

      {/* Dropdown with search results */}
      {isDropdownOpen && !isDisabled && (
        <Box
          w="100%"
          pos="absolute"
          zIndex="1000"
          top={{
            base: `calc(44px + ${labelOffset})`,
            md: `calc(${height} + ${labelOffset})`,
          }}
          overflow="hidden"
          bg="white"
          py="6px"
          boxShadow="0px 6px 35px 5px #0000001F"
          borderColor="neutral.100"
          borderWidth="1px"
          borderRadius="8px"
          maxHeight={maxHeight}
          overflowY="auto"
          // Prevent the input from losing focus when clicking dropdown items
          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        >
          {!members.isFetching && filteredMembers.length > 0 && (
            <>
              <Flex px="16px" py="8px" alignItems="center" justifyContent="space-between" gap="12px">
                <Text
                  as="button"
                  type="button"
                  fontSize="12px"
                  fontWeight="600"
                  color={areAllSelected ? 'neutral.300' : 'primary.500'}
                  cursor={areAllSelected ? 'not-allowed' : 'pointer'}
                  _hover={areAllSelected ? undefined : { color: 'primary.600' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!areAllSelected) handleSelectAll()
                  }}
                >
                  Select all
                </Text>
                <Text
                  as="button"
                  type="button"
                  fontSize="12px"
                  fontWeight="600"
                  color={(value?.length ?? 0) === 0 ? 'neutral.300' : 'primary.500'}
                  cursor={(value?.length ?? 0) === 0 ? 'not-allowed' : 'pointer'}
                  _hover={(value?.length ?? 0) === 0 ? undefined : { color: 'primary.600' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if ((value?.length ?? 0) > 0) handleUnselectAll()
                  }}
                >
                  Unselect all
                </Text>
              </Flex>
              <Divider m="0" />
            </>
          )}

          {members.isFetching && (
            <Flex h="64px" p="2px 16px" alignItems="center" justifyContent="center">
              <Text w="100%" textAlign="center" textStyle="b-sm" color="neutral.500">
                Loading...
              </Text>
            </Flex>
          )}

          {!members.isFetching && filteredMembers.length === 0 && (
            <Flex h="64px" p="2px 16px" alignItems="center">
              <Text w="100%" textAlign="center" textStyle="b-sm" color="neutral.500">
                No members found
              </Text>
            </Flex>
          )}

          {!members.isFetching &&
            filteredMembers.map((member, i) => (
              <Box key={member.id}>
                <Flex
                  px="16px"
                  h="48px"
                  cursor="pointer"
                  alignItems="center"
                  transition="0.2s ease-out"
                  justifyContent="flex-start"
                  py="8px"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => handleToggleMember(member)}
                >
                  <Checkbox
                    isChecked={isMemberSelected(member)}
                    onChange={() => handleToggleMember(member)}
                    size="md"
                    colorScheme="primary"
                    mr="12px"
                    pointerEvents="none"
                  />
                  <LazyLoadAvatar src={member.image} w={24} h={24} name={member.name} />
                  <Box ml="8px">
                    <Text noOfLines={1} textStyle="b-md" color="neutral.900">
                      {member.name}
                    </Text>
                  </Box>
                </Flex>
                {i !== filteredMembers.length - 1 && <Divider m="0" />}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  )
}

// Custom isEqual function for memo optimization
const isEqual = (
  prevProps: Readonly<FormMemberMultiSelectProps>,
  nextProps: Readonly<FormMemberMultiSelectProps>,
) => {
  return (
    JSON.stringify(prevProps.value) === JSON.stringify(nextProps.value) &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.name === nextProps.name &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.hideLabel === nextProps.hideLabel
  )
}

export default memo(FormMemberMultiSelect, isEqual)
